import { useCallback, useLayoutEffect, useRef, useState } from "react";
import { IconButton, Tooltip, Grid, useTheme } from "@mui/material";
import { useDispatch } from "react-redux";
import styled from "styled-components";

import AiLogo from "@/ai/AiLogo";
import useEntitlements from "@/auth/useEntitlements";
import { FEATURE_AI_INTAKE } from "@/constants";
import CreateButton from "@/nav/CreateButton";
import GlobalSearch from "@/nav/GlobalSearch";
import LogoButton from "@/nav/LogoButton";
import MobileNavButton from "@/nav/MobileNavButton";
import {
    desktopTopNavHorizontalPadding,
    desktopTopNavZIndex,
    mobileTopNavZIndex,
} from "@/nav/navConstants";
import { NavProvider, useNavContext } from "@/nav/NavContext";
import { openProjectWorkshop } from "@/nav/navSlice";
import { getSideNavColors } from "@/nav/navTheme";
import ThemeSwitch from "@/nav/ThemeSwitch";
import { PERMISSION_LLM_WORKFLOW_BACKDOOR } from "@/auth/constants";

const NavBar = styled.div`
    position: ${(props) => (props.isSmall ? "fixed" : "absolute")};
    top: 0;
    left: 0px;
    right: 0px;
    z-index: ${(props) =>
        props.isSmall ? mobileTopNavZIndex : desktopTopNavZIndex};
    box-sizing: border-box;
    height: ${(props) => (props.isSmall ? "55px" : "auto")};
    padding: ${(props) =>
        props.isSmall
            ? "0 20px"
            : `16.5px ${desktopTopNavHorizontalPadding}px 0px ${desktopTopNavHorizontalPadding}px`};
    background: ${(props) =>
        props.isSmall ? props.backgroundColor : "transparent"};
    display: ${(props) => (props.isSmall ? "flex" : "block")};
    align-items: ${(props) => (props.isSmall ? "center" : "initial")};
`;

const RightNav = styled.div`
    float: right;
    margin-right: -10px;
    display: flex;
    align-items: center;
    gap: 20px;
    margin-top: 10.5px;
`;

const NavActions = styled.div`
    display: flex;
    align-items: center;
    gap: 20px;
    height: 30px;
`;

export default function TopNav({ toggleTheme }) {
    return (
        <NavProvider>
            <TopNavContent toggleTheme={toggleTheme} />
        </NavProvider>
    );
}

function TopNavContent({ toggleTheme }) {
    const {
        state: { isHidden, isSmall, open: isNavOpen },
    } = useNavContext();

    if (isHidden) {
        return null;
    }

    return isSmall ? (
        <MobileTopNav />
    ) : (
        <DesktopTopNav isNavOpen={isNavOpen} toggleTheme={toggleTheme} />
    );
}

function DesktopTopNav({ isNavOpen, toggleTheme }) {
    const actionsRef = useRef(null);
    const [rightActionsWidth, setRightActionsWidth] = useState(0);

    useLayoutEffect(() => {
        const actionsNode = actionsRef.current;

        if (!actionsNode) {
            return undefined;
        }

        const updateActionsWidth = () => {
            const nextWidth = Math.ceil(
                actionsNode.getBoundingClientRect().width,
            );

            setRightActionsWidth((previousWidth) =>
                previousWidth == nextWidth ? previousWidth : nextWidth,
            );
        };

        updateActionsWidth();

        if (typeof ResizeObserver == "undefined") {
            window.addEventListener("resize", updateActionsWidth);

            return () => {
                window.removeEventListener("resize", updateActionsWidth);
            };
        }

        const observer = new ResizeObserver(updateActionsWidth);
        observer.observe(actionsNode);

        return () => {
            observer.disconnect();
        };
    }, []);

    return (
        <NavBar>
            <RightNav ref={actionsRef}>
                <TopNavActions toggleTheme={toggleTheme} />
            </RightNav>
            <GlobalSearch
                isNavOpen={isNavOpen}
                rightActionsWidth={rightActionsWidth}
            />
        </NavBar>
    );
}

function TopNavActions({ toggleTheme }) {
    const dispatch = useDispatch();
    const theme = useTheme();
    const { hasFeature, hasPermission } = useEntitlements();
    const llmWorkflowBackdoorPermission = hasPermission(
        PERMISSION_LLM_WORKFLOW_BACKDOOR,
    );
    const showLlmWorkflow =
        llmWorkflowBackdoorPermission || hasFeature(FEATURE_AI_INTAKE);

    const handleProjectWorkshopOpen = useCallback(() => {
        dispatch(
            openProjectWorkshop({
                open: true,
                threadId: null,
                locked: false,
            }),
        );
    }, [dispatch]);

    return (
        <NavActions>
            <ThemeSwitch
                checked={theme.palette.mode != "dark"}
                onChange={toggleTheme}
                inputProps={{ "aria-label": "Toggle theme" }}
            />
            {showLlmWorkflow && (
                <AiQuickStartButton onClick={handleProjectWorkshopOpen} />
            )}
            <div>
                <CreateButton />
            </div>
        </NavActions>
    );
}

function AiQuickStartButton({ onClick }) {
    const theme = useTheme();

    return (
        <Tooltip title="AI Quick Start">
            <IconButton
                data-testid="ai-quick-start-button"
                aria-label="AI Quick Start"
                onClick={onClick}
                disableRipple
                sx={{
                    width: 30,
                    height: 30,
                    padding: 0,
                    borderRadius: "100px",
                    backgroundColor: theme.palette.secondary.main,
                    transition: theme.transitions.create(
                        ["background-color", "transform"],
                        {
                            easing: theme.transitions.easing.easeOut,
                            duration: 300,
                        },
                    ),
                    "&:hover": {
                        backgroundColor: theme.palette.secondary.light,
                    },
                    "&:active": {
                        transform: "scale(0.97)",
                    },
                    "& img": {
                        display: "block",
                        width: 14,
                        height: 14,
                    },
                }}
            >
                <AiLogo />
            </IconButton>
        </Tooltip>
    );
}

function MobileTopNav() {
    const theme = useTheme();
    const colors = getSideNavColors(theme);

    return (
        <NavBar isSmall={true} backgroundColor={colors.mobileBackground}>
            <Grid
                container
                justifyContent={"space-between"}
                alignItems={"center"}
                width={"100%"}
            >
                <Grid item>
                    <LogoButton
                        sx={{
                            display: "block",
                            width: "auto",
                            height: "9.46px",
                        }}
                    />
                </Grid>
                <Grid item>
                    <MobileNavButton />
                </Grid>
            </Grid>
        </NavBar>
    );
}
